/**
 * Microdose — age + residency gate, cookie persistence, cart reconcile.
 *
 * Rules/config: assets/microdose-residency-config.js
 *                snippets/microdose-residency-config.liquid
 *
 * FUTURE agents: when moving to a full state dropdown + product tags,
 * extend MicrodoseResidencyConfig first, then wire gate UI + isProductEligible
 * into reconcileCart / buy swatch filtering. Avoid new hardcoded oregon checks here.
 */
(function () {
  'use strict';

  var CFG =
    (typeof window !== 'undefined' && window.MicrodoseResidencyConfig) || {
      cookieKey: 'md_residency',
      attrKey: 'md_residency',
      ageCookieKey: 'md_age_ok',
      cookieDays: 365,
      defaultResidency: 'oregon',
      knownResidencies: ['oregon', 'other'],
      packByResidency: { oregon: '60ct', other: '50ct' },
      labelByResidency: { oregon: 'OR', other: 'US' },
      packValues: ['60ct', '50ct', '20ct'],
      normalizeResidency: function (v) {
        return v === 'other' ? 'other' : 'oregon';
      },
      packForResidency: function (v) {
        return v === 'other' ? '50ct' : '60ct';
      },
      labelForResidency: function (v) {
        return v === 'other' ? 'US' : 'OR';
      },
      isProductEligible: function () {
        return true;
      },
    };

  var productCache = {};

  /*
    Circuit breaker for the automatic residency-reconcile reloads below.
    Each one fires on ITS OWN page load when the server-rendered cart
    attribute doesn't match the residency cookie — intended as a one-shot
    "sync then refresh". If the write doesn't take effect in time for the
    next server render (edge caching, a slow /cart/update.js under flaky
    networking), the reloaded page detects the exact same mismatch and
    reloads again — an infinite loop with no user action involved. Capping
    each reason to one reload per browser session makes that impossible
    while still allowing a genuine future mismatch (new tab/session) to
    resync normally.
  */
  function reloadOnce(reason) {
    var key = 'md_reload_guard_' + reason;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {}
    window.location.reload();
  }

  function clearReloadGuard(reason) {
    try {
      sessionStorage.removeItem('md_reload_guard_' + reason);
    } catch (e) {}
  }

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : '';
  }

  function setCookie(name, value, days) {
    var maxAge = Math.floor((days || CFG.cookieDays || 365) * 86400);
    document.cookie =
      name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
  }

  function getResidency() {
    var fromCookie = getCookie(CFG.cookieKey);
    if (CFG.knownResidencies.indexOf(fromCookie) !== -1) return fromCookie;
    return '';
  }

  function packForResidency(residency) {
    return CFG.packForResidency(residency || CFG.defaultResidency);
  }

  function labelForResidency(residency) {
    return CFG.labelForResidency(residency || CFG.defaultResidency);
  }

  function parseJsonResponse(res) {
    return res.text().then(function (text) {
      var data = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = null;
        }
      }
      if (!res.ok) {
        throw new Error(
          (data && (data.description || data.message)) ||
            (res.status === 401
              ? 'Cart session expired. Refresh the page and try again.'
              : 'Request failed (' + res.status + ')')
        );
      }
      if (!data) {
        throw new Error('Empty cart response. Refresh the page and try again.');
      }
      return data;
    });
  }

  function syncCartAttribute(residency) {
    var attrs = {};
    attrs[CFG.attrKey] = residency;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes: attrs }),
    }).then(parseJsonResponse);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(parseJsonResponse);
  }

  function itemHandle(item) {
    if (item && item.handle) return item.handle;
    var m = ((item && item.url) || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function packFromText(value) {
    var text = String(value || '').toLowerCase();
    var packs = CFG.packValues || ['60ct', '50ct', '20ct'];
    for (var i = 0; i < packs.length; i++) {
      if (text.indexOf(String(packs[i]).toLowerCase()) !== -1) return packs[i];
    }
    return '';
  }

  function itemPack(item) {
    return (
      packFromText(item.variant_title) ||
      packFromText(item.title) ||
      packFromText(item.public_title) ||
      ''
    );
  }

  function variantPack(variant) {
    if (!variant) return '';
    var fromTitle = packFromText(variant.title);
    if (fromTitle) return fromTitle;
    if (variant.options && variant.options.length) {
      for (var i = 0; i < variant.options.length; i++) {
        var fromOpt = packFromText(variant.options[i]);
        if (fromOpt) return fromOpt;
      }
    }
    return '';
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (!r.ok) return null;
        return r.json();
      })
      .catch(function () {
        return null;
      });
    return productCache[handle];
  }

  function findTargetVariant(product, pack) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (variantPack(product.variants[i]) === pack) return product.variants[i];
    }
    return null;
  }

  function targetSupportsSellingPlan(variant, sellingPlanId) {
    if (!sellingPlanId || !variant || !variant.selling_plan_allocations) return false;
    for (var i = 0; i < variant.selling_plan_allocations.length; i++) {
      var alloc = variant.selling_plan_allocations[i];
      if (alloc && alloc.selling_plan_id && Number(alloc.selling_plan_id) === Number(sellingPlanId)) {
        return true;
      }
      if (
        alloc &&
        alloc.selling_plan &&
        Number(alloc.selling_plan.id) === Number(sellingPlanId)
      ) {
        return true;
      }
    }
    return false;
  }

  function clearLines(keys) {
    if (!keys.length) return Promise.resolve();
    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates: updates }),
    }).then(parseJsonResponse);
  }

  function addItems(items) {
    if (!items.length) return Promise.resolve();
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: items }),
    }).then(parseJsonResponse).then(function (data) {
      if (data.status) {
        throw new Error(data.description || data.message || 'Cart add failed');
      }
      return data;
    });
  }

  /**
   * Reconcile cart to residency pack rules.
   * FUTURE: also remove lines where !CFG.isProductEligible(product, residency).
   */
  function reconcileCart(residency) {
    var active = CFG.normalizeResidency(residency || CFG.defaultResidency);
    var pack = packForResidency(active);

    return fetchCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) {
        return { updated: false, count: 0 };
      }

      var removeKeys = [];
      var addPayload = [];

      return cart.items
        .reduce(function (chain, item) {
          return chain.then(function () {
            var handle = itemHandle(item);

            // Split-product pairs: Oregon/national versions are separate
            // products (own shipping profiles). The Liquid-emitted
            // window.MicrodosePairMap carries the metafield data that
            // /products/{handle}.js can't expose: handle -> {pair, loc}.
            var pairInfo = (window.MicrodosePairMap || {})[handle];
            if (pairInfo && pairInfo.loc === active) {
              // Already the residency-correct sibling — leave the line alone.
              return;
            }
            if (pairInfo && pairInfo.pair && pairInfo.loc && pairInfo.loc !== active) {
              return fetchProduct(pairInfo.pair).then(function (pairProduct) {
                if (!pairProduct || !pairProduct.variants || !pairProduct.variants.length) {
                  // Sibling unavailable (misconfigured/unpublished) — leave the
                  // line so checkout surfaces the problem instead of silently
                  // dropping the item.
                  return;
                }
                var target = findTargetVariant(pairProduct, pack) || pairProduct.variants[0];
                var sellingPlanId =
                  item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
                    ? item.selling_plan_allocation.selling_plan.id
                    : null;
                removeKeys.push(item.key);
                var addItem = {
                  id: Number(target.id),
                  quantity: item.quantity,
                };
                if (sellingPlanId && targetSupportsSellingPlan(target, sellingPlanId)) {
                  addItem.selling_plan = Number(sellingPlanId);
                }
                addPayload.push(addItem);
              });
            }

            return fetchProduct(handle).then(function (product) {
              // FUTURE: product-level state eligibility
              if (product && CFG.isProductEligible && !CFG.isProductEligible(product, active)) {
                removeKeys.push(item.key);
                return;
              }

              var currentPack = itemPack(item);
              if (!currentPack) return;
              if (currentPack === pack) return;

              var target = findTargetVariant(product, pack);
              var sellingPlanId =
                item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
                  ? item.selling_plan_allocation.selling_plan.id
                  : null;

              removeKeys.push(item.key);

              if (!target) return;

              var addItem = {
                id: Number(target.id),
                quantity: item.quantity,
              };

              if (sellingPlanId && targetSupportsSellingPlan(target, sellingPlanId)) {
                addItem.selling_plan = Number(sellingPlanId);
              }

              addPayload.push(addItem);
            });
          });
        }, Promise.resolve())
        .then(function () {
          if (!removeKeys.length) return { updated: false, count: 0 };

          return clearLines(removeKeys)
            .then(function () {
              return addItems(addPayload);
            })
            .then(function () {
              return { updated: true, count: removeKeys.length };
            });
        });
    });
  }

  var CART_NOTICES = {
    '1': 'Cart updated for your shipping state',
    /* Set by the checkout "Switch to national packs" return link */
    switch: 'Switched to our national 50ct packs. You can check out now.',
    switchOregon: 'Switched to our Oregon 60ct packs. You can check out now.',
  };

  function showCartNotice(kind) {
    document.cookie = 'md_cart_notice=' + (kind || '1') + '; path=/; max-age=120; SameSite=Lax';
  }

  function maybeShowCartNotice() {
    var kind = getCookie('md_cart_notice');
    if (!kind || !CART_NOTICES[kind]) return;
    document.cookie = 'md_cart_notice=; path=/; max-age=0; SameSite=Lax';

    var notice = document.createElement('div');
    notice.className = 'md-gate__toast';
    notice.setAttribute('role', 'status');
    notice.textContent = CART_NOTICES[kind];
    document.body.appendChild(notice);
    requestAnimationFrame(function () {
      notice.classList.add('is-visible');
    });
    setTimeout(function () {
      notice.classList.remove('is-visible');
      setTimeout(function () {
        if (notice.parentNode) notice.parentNode.removeChild(notice);
      }, 300);
    }, 3200);
  }

  function setResidencyAndEnter(residency, options) {
    options = options || {};
    var normalized = CFG.normalizeResidency(residency);
    setCookie(CFG.cookieKey, normalized);
    setCookie(CFG.ageCookieKey, '1');

    var reconcile = options.reconcile !== false;

    return syncCartAttribute(normalized)
      .then(function () {
        if (!reconcile) return { updated: false, count: 0 };
        return reconcileCart(normalized);
      })
      .then(function (result) {
        if (result && result.updated) showCartNotice(options.notice);
        window.location.reload();
      })
      .catch(function (err) {
        console.error('Residency update failed', err);
        window.location.reload();
      });
  }

  function lockScroll(lock) {
    document.documentElement.classList.toggle('md-gate-open', lock);
    document.body.classList.toggle('md-gate-open', lock);
  }

  function syncHeaderPill() {
    var value = getResidency() || CFG.defaultResidency;
    var label = labelForResidency(value);
    document.querySelectorAll('[data-md-open-residency]').forEach(function (el) {
      el.setAttribute('data-md-residency', value);
      el.setAttribute('aria-label', 'Change shopping state. Currently shopping from ' + label);
      var labelEl = el.querySelector('[data-md-residency-label]');
      if (labelEl) labelEl.textContent = label;
      else el.textContent = 'Shopping from: ' + label;
    });
  }

  function clearGateCookies() {
    [CFG.cookieKey, CFG.ageCookieKey, 'md_cart_notice'].forEach(function (name) {
      document.cookie = name + '=; path=/; max-age=0; SameSite=Lax';
    });
  }

  /*
    Checkout return link: /cart?md_switch=other. The inline gate script has
    already set the cookies (window.__mdPendingSwitch); here we drop the
    param so the reload can't repeat it, swap Oregon packs for their national
    siblings, and reload onto the updated cart.
  */
  function handleSwitchLink() {
    var target = window.__mdPendingSwitch;
    if (!target) {
      try {
        var param = new URLSearchParams(window.location.search).get('md_switch');
        if (param === 'other' || param === 'oregon') target = param;
      } catch (e) {}
    }
    if (!target) return false;
    window.__mdPendingSwitch = null;
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('md_switch');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {}
    var working = document.createElement('div');
    working.className = 'md-gate__toast is-visible';
    working.setAttribute('role', 'status');
    working.textContent = target === 'oregon' ? 'Switching to Oregon packs…' : 'Switching to national packs…';
    (document.body || document.documentElement).appendChild(working);
    setResidencyAndEnter(target, { notice: target === 'oregon' ? 'switchOregon' : 'switch' });
    return true;
  }

  function initGate() {
    var root = document.getElementById('md-entry-gate');
    if (!root) return;
    if (handleSwitchLink()) return;

    // Testing: ?md_reset_gate=1 clears gate cookies and forces the quiz open.
    var forceReset =
      typeof URLSearchParams !== 'undefined' &&
      new URLSearchParams(window.location.search).has('md_reset_gate');
    if (forceReset) clearGateCookies();

    var stepAge = root.querySelector('[data-md-gate-step="age"]');
    var stepResidency = root.querySelector('[data-md-gate-step="residency"]');
    var stepDenied = root.querySelector('[data-md-gate-step="denied"]');
    var progressDots = root.querySelectorAll('[data-md-gate-dot]');
    var steps = [stepAge, stepResidency, stepDenied].filter(Boolean);
    var quizOrder = ['age', 'residency'];
    var animating = false;
    var reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var residency = forceReset ? '' : getResidency();
    var ageOk = forceReset ? false : getCookie(CFG.ageCookieKey) === '1';

    function panelFor(name) {
      return root.querySelector('[data-md-gate-step="' + name + '"]');
    }

    function updateProgress(name) {
      var idx = quizOrder.indexOf(name);
      progressDots.forEach(function (dot) {
        var key = dot.getAttribute('data-md-gate-dot');
        var dotIdx = quizOrder.indexOf(key);
        dot.classList.toggle('is-active', key === name);
        dot.classList.toggle('is-complete', idx > -1 && dotIdx > -1 && dotIdx < idx);
      });
    }

    function focusStep(name) {
      // Focus the dialog shell — not a CTA — so Dawn's focus halo doesn't flash on open.
      var dialog = root.querySelector('.md-gate__dialog');
      if (!dialog) return;
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      try {
        dialog.focus({ preventScroll: true });
      } catch (e) {
        dialog.focus();
      }
    }

    function settleStep(name) {
      steps.forEach(function (el) {
        var on = el.getAttribute('data-md-gate-step') === name;
        el.hidden = !on;
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
        el.classList.remove('is-animating-in', 'is-animating-out', 'is-active');
        if (on) el.classList.add('is-active');
        var title = el.querySelector('.md-gate__title');
        if (title) {
          if (on) title.id = 'md-gate-title';
          else title.removeAttribute('id');
        }
      });
      root.setAttribute('data-active-step', name);
      updateProgress(name);
      focusStep(name);
      animating = false;
    }

    function showStep(name, options) {
      options = options || {};
      var next = panelFor(name);
      if (!next) return;

      var currentName = root.getAttribute('data-active-step') || '';
      var current = panelFor(currentName);
      var instant = options.instant || reduceMotion || !current || currentName === name || root.hidden;

      if (instant || animating) {
        settleStep(name);
        return;
      }

      animating = true;
      next.hidden = false;
      next.setAttribute('aria-hidden', 'false');
      next.classList.remove('is-animating-out');
      next.classList.add('is-animating-in');
      current.classList.remove('is-animating-in', 'is-active');
      current.classList.add('is-animating-out');
      root.setAttribute('data-active-step', name);
      updateProgress(name);

      var finished = false;
      function finish() {
        if (finished) return;
        finished = true;
        next.removeEventListener('animationend', onEnd);
        settleStep(name);
      }
      function onEnd(e) {
        if (e.target !== next) return;
        finish();
      }
      next.addEventListener('animationend', onEnd);
      setTimeout(finish, 320);
    }

    function openGate(step, options) {
      options = options || {};
      if (root._mdLeaveTimer) {
        clearTimeout(root._mdLeaveTimer);
        root._mdLeaveTimer = null;
      }
      root.classList.remove('is-leaving');
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      // Double rAF so the browser paints the closed state before entrance anim runs
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          root.classList.add('is-open');
          lockScroll(true);
        });
      });
      showStep(step || 'age', { instant: options.instant !== false });
    }

    function closeGate() {
      if (root.hidden && !root.classList.contains('is-open')) {
        lockScroll(false);
        return;
      }
      root.classList.add('is-leaving');
      root.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (root._mdLeaveTimer) clearTimeout(root._mdLeaveTimer);
      root._mdLeaveTimer = setTimeout(function () {
        root.hidden = true;
        root.classList.remove('is-open', 'is-leaving');
        root._mdLeaveTimer = null;
      }, reduceMotion ? 0 : 280);
    }

    function ensureCartMatchesResidency(activeResidency) {
      return reconcileCart(activeResidency).then(function (result) {
        if (result && result.updated) {
          showCartNotice();
          reloadOnce('md_cart_reconciled');
          return true;
        }
        clearReloadGuard('md_cart_reconciled');
        return false;
      });
    }

    function onKeydown(e) {
      if (root.hidden || e.key !== 'Tab') return;
      var focusables = root.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var list = Array.prototype.filter.call(focusables, function (el) {
        return el.offsetParent !== null && !el.closest('[hidden]');
      });
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);

    if (window.Shopify && Shopify.designMode) {
      closeGate();
    } else if (!residency) {
      // Entry quiz: wait ~5s so the homepage paints first (owner request).
      // Manual "Shopping from" switcher stays instant via openSwitcher().
      root.setAttribute('data-md-gate-mode', 'entry');
      closeGate();
      if (window.__mdEntryGateTimer) {
        clearTimeout(window.__mdEntryGateTimer);
        window.__mdEntryGateTimer = null;
      }
      var entryDelay = parseInt(root.getAttribute('data-md-gate-entry-delay') || '5000', 10);
      if (forceReset) entryDelay = 0;
      if (isNaN(entryDelay) || entryDelay < 0) entryDelay = 5000;

      window.__mdEntryGateTimer = setTimeout(function () {
        if (getResidency()) return;
        window.__mdEntryGateOpenedByJs = true;
        if (ageOk) {
          openGate('residency');
        } else {
          openGate('age');
        }
      }, entryDelay);
    } else {
      closeGate();
      var attrResidency = root.getAttribute('data-md-residency') || '';
      var boot = Promise.resolve();
      if (attrResidency !== residency) {
        boot = syncCartAttribute(residency);
      }
      boot
        .then(function () {
          return ensureCartMatchesResidency(residency);
        })
        .then(function (reloaded) {
          if (!reloaded && attrResidency !== residency) {
            reloadOnce('md_attr_mismatch');
          } else {
            clearReloadGuard('md_attr_mismatch');
          }
        })
        .catch(function () {});
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-md-gate-action]');
      if (!btn) return;
      var action = btn.getAttribute('data-md-gate-action');

      if (action === 'age-yes') {
        setCookie(CFG.ageCookieKey, '1');
        showStep('residency');
        return;
      }
      if (action === 'age-no') {
        showStep('denied');
        return;
      }
      // FUTURE: data-md-gate-action="residency-set" data-md-residency="oregon|other|..."
      if (action === 'residency-oregon') {
        btn.disabled = true;
        setResidencyAndEnter('oregon', { reconcile: true });
        return;
      }
      if (action === 'residency-other') {
        btn.disabled = true;
        setResidencyAndEnter('other', { reconcile: true });
        return;
      }
      if (action === 'residency-set') {
        btn.disabled = true;
        setResidencyAndEnter(btn.getAttribute('data-md-residency') || CFG.defaultResidency, {
          reconcile: true,
        });
      }
    });

    window.microdoseResidency = {
      get: getResidency,
      pack: function () {
        return packForResidency(getResidency() || CFG.defaultResidency);
      },
      label: function () {
        return labelForResidency(getResidency() || CFG.defaultResidency);
      },
      openSwitcher: function () {
        if (window.__mdEntryGateTimer) {
          clearTimeout(window.__mdEntryGateTimer);
          window.__mdEntryGateTimer = null;
        }
        window.__mdEntryGateOpenedByJs = true;
        root.setAttribute('data-md-gate-mode', 'switch');
        openGate('residency');
      },
      set: setResidencyAndEnter,
      reconcile: reconcileCart,
      config: CFG,
    };

    function closeMobileMenu() {
      var drawer = document.querySelector('header-drawer');
      if (drawer && typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer();
      }
    }

    document.querySelectorAll('[data-md-open-residency]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closeMobileMenu();
        window.microdoseResidency.openSwitcher();
      });
    });

    syncHeaderPill();
    maybeShowCartNotice();
  }

  /**
   * Oregon shoppers: hide 3mg / entry-level collection links from nav + drawers.
   * Shop-all cards are filtered server-side in category-hub.
   */
  function hideOregonRestrictedNav() {
    var residency = '';
    var pill = document.querySelector('[data-md-residency]');
    if (pill) residency = String(pill.getAttribute('data-md-residency') || '').toLowerCase();
    if (!residency) {
      var match = document.cookie.match(/(?:^|; )md_residency=(oregon|other)(?:;|$)/);
      residency = match ? match[1] : 'oregon';
    }
    if (residency !== 'oregon') return;

    var nodes = document.querySelectorAll(
      'a[href*="entry-level-doses"], a[href*="/collections/entry-level"]'
    );
    Array.prototype.forEach.call(nodes, function (a) {
      var text = String(a.textContent || '').toLowerCase();
      var href = String(a.getAttribute('href') || '').toLowerCase();
      var is3mg =
        href.indexOf('entry-level') !== -1 ||
        text.indexOf('3mg') !== -1 ||
        text.indexOf('original microdose') !== -1;
      if (!is3mg) return;
      var wrap = a.closest('li, .mega-menu__item, .header__submenu li') || a;
      wrap.setAttribute('hidden', '');
      wrap.style.display = 'none';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initGate();
      hideOregonRestrictedNav();
    });
  } else {
    initGate();
    hideOregonRestrictedNav();
  }

  window.addEventListener('microdose:residency-changed', hideOregonRestrictedNav);
})();
